"use client";

/** 入力シートのヘルプの文面。受け口が help として返す */
export interface Help {
  how: [string, string][];
  notes: string[];
  options: [string, string][];
}

/** 1 行。文だけの行と、[名, 説明] の行 */
type Line = string | [string, string];

interface Block {
  head?: string;
  /** [文, 例] の行として出す。文を太字にせず、例を小さく添える */
  example?: boolean;
  lines: Line[];
}

interface Chapter {
  title: string;
  blocks: Block[];
}

/** 行動の書き方より前に並べる章 */
const BEFORE: Chapter[] = [
  {
    title: "遊びの流れ",
    blocks: [
      {
        lines: [
          "キャラクターにさせたいことを文章で書くと、その続きが物語になって届きます。これを繰り返して、キャラクターの物語を進めます。",
          "1. 画面の下のバー「行動を入力する」を押して、行動を書きます。",
          "2. 確定 を押します。バーが「行動を確定しました」に替わります。進行が始まるまでは取り消せます。",
          "3. 進行が始まると、バーが「物語を紡いでいます」に替わります。この間は入力できません。",
          "4. 進行が終わると、メインに新しい物語が並びます。次の行動を書けるようになります。",
          "確定した後は、画面を閉じても進みます。開いたままの画面は、物語が届くと自動で替わります。",
        ],
      },
    ],
  },
  {
    title: "画面の見方",
    blocks: [
      {
        lines: [
          ["上の鈴のボタン", "お知らせを開きます。未読があると赤い印が付きます"],
          ["上の右端のボタン", "キャラ選択画面へ戻る・アカウント情報・ログアウト を選べます"],
          ["下の 5 つのボタン", "辞書・キャラ情報・メイン・マップ・その他 の画面を切り替えます"],
          ["下のバー", "行動の入力です。辞書・キャラ情報・メイン・マップ の画面に出ます。押すと開き、もう一度押すと畳みます"],
        ],
      },
    ],
  },
];

/** 行動の書き方より後に並べる章 */
const AFTER: Chapter[] = [
  {
    title: "バーの表示",
    blocks: [
      {
        lines: [
          ["行動を入力する", "行動を書けます"],
          ["書きかけの行動があります", "書いた文が残っています。確定はまだです"],
          ["フルオートで進める", "おまかせオプションがフルオートです。入力を使わずに確定できます"],
          ["行動を確定しました", "進行が始まるのを待っています。開くと 確定を取り消す を押せます"],
          ["物語を紡いでいます", "進行中です。終わるまで開けません"],
          ["戦闘中", "戦闘が始まっています。押すと戦闘の画面へ移ります"],
          ["作製中", "作製が始まっています。押すと作製の画面へ移ります"],
          ["戦闘の結末を物語にします／作製の結末を物語にします", "結末は次の進行で物語になります。そのまま待ちます"],
          ["キャラを手直ししています", "手直し依頼の作業中です。終わるまで開けません"],
          ["味方キャラ作成中につき停止中", "仲間になったキャラの準備中です。終わるまで開けません"],
          ["ゲームオーバー", "キャラクターが死亡しました。行動は入力できません"],
        ],
      },
    ],
  },
  {
    title: "メイン",
    blocks: [
      {
        lines: [
          "いちばん新しい物語が並びます。上にキャラクターの名と、今いる場所の日時が出ます。",
          "これまでの物語は、その他 の ログ の 場面 で読み返せます。",
          "文字の大きさと紙の見た目は、その他 の 設定 で替えられます。",
        ],
      },
    ],
  },
  {
    title: "キャラ情報",
    blocks: [
      {
        lines: [
          "自分のキャラクターと、同行者（一緒に旅をする仲間）が並びます。押すと中身が開きます。",
          "「作成中」の札が付いた仲間は準備中です。準備が終わると開けます。",
        ],
      },
      {
        head: "プロフィール",
        lines: ["外見・経歴など、キャラクターの人となりです。"],
      },
      {
        head: "ステータス",
        lines: [
          "上に HP などの今の値・付いている状態・現在地が出ます。下の 所持金・基礎ステータス・判定ステータス・耐性 は、押すと開きます。",
          ["振り分けポイント", "成長で手に入ります。基礎ステータスの ＋ と − で振り、決める を押します。決めた後は戻せません。同行者の分は見るだけです"],
          ["防御の枠", "物理防御と魔法防御は、肉体の枠と装備の枠の和です。枠はそれぞれ 50 まで効き、超えた分は被弾を下げます"],
          ["耐性", "1.0 が普通です。数が大きいほど効きにくく、小さいほど効きやすくなります"],
        ],
      },
      {
        head: "インベントリ",
        lines: [
          ["装備", "部位ごとに着けている物が並びます。変更 を押すと、外す物か、着ける物を選べます"],
          ["所持品", "装備していない持ち物です。分類のボタンを押すと、その分類の物だけに絞り込めます。物を開いて 捨てる を押すと、捨てる予定になります。積載 は、持っている量と持てる量です。持てる量は 70 ＋ 筋力 に、腰の袋・背負い袋の分が足されます"],
          ["要る筋力", "積載 20 以上の物は、持つにも着けるにも 積載 × 1.5 の筋力が要ります"],
          "付け替えと破棄は「次の物語に入る変更」に並び、次の進行で物語に入ります。それまでは 元に戻す で取り消せます。",
          "行動を入力できない間は、付け替えと破棄ができません。",
        ],
      },
      {
        head: "スキル",
        lines: [
          ["一覧", "持っているスキルの全てです。押すと効果が開きます"],
          ["戦闘用", "戦闘に持っていくスキルを選びます。選べるスキル を開いて 選ぶ を押し、決める を押します。持っていける数は「選んだスキル」の横に出ます"],
          ["戦闘パッシブスキル", "選ばなくても戦闘で効きます"],
          ["体得", "体得したものと、そこから覚えられるスキルです"],
          ["使えない", "そのスキルに要る装備を着けていません"],
        ],
      },
      {
        head: "関係",
        lines: ["同行者にだけ出ます。相手ごとの関係・人物像・経緯です。"],
      },
      {
        head: "イベント",
        lines: [
          "自分のキャラクターにだけ出ます。関わっている出来事が 進行中 と 完了 に分かれて並びます。",
          "押すと、キャラクターに分かっていることと、これまでの経過が出ます。",
        ],
      },
    ],
  },
  {
    title: "辞書",
    blocks: [
      {
        lines: ["上のタブで 知見辞書 と 戦闘辞書 を切り替えます。並びの上の欄に名を入れると探せます。"],
      },
      {
        head: "知見辞書",
        lines: ["キャラクターが物語の中で見て知ったことが載ります。物語が進むと増えます。名を押すと本文が開きます。"],
      },
      {
        head: "戦闘辞書",
        lines: [
          "魔物の一族が並び、横に 開いた魔物の数／全ての魔物の数 が出ます。一族を押すと、その一族の魔物が並びます。戻る で一族の並びへ戻ります。",
          "名を知らない魔物は ？？？？？ か、戦ったときの呼び名で出ます。",
          "知っている魔物は、押すと 危なさ・姿・生息地・弱点・落とす物 が開きます。薄く出る魔物は、まだ読めません。",
          "一族の魔物のうち 危なさ ★1〜★3 のものは、初めから名も中身も開いています。",
          "魔物のほかの敵（獣・人）と一族に入らない魔物は その他 に並びます。その他 の敵は、初めは名も中身も閉じています。",
          "戦った魔物は中身が開きます。名は、人に教わる・図鑑を読む・魔物の知識を体得する と分かります。",
        ],
      },
    ],
  },
  {
    title: "マップ",
    blocks: [
      {
        lines: [
          ["近く・全体", "現在地の周りと、世界の全体を切り替えます"],
          ["拡大", "指でつまむ・ホイール・ダブルクリック"],
          ["移動", "ドラッグ"],
          ["現在地情報", "今いる土地の説明が開きます"],
        ],
      },
    ],
  },
  {
    title: "戦闘",
    blocks: [
      {
        lines: [
          "物語の中で戦闘が始まると、下のバーが「戦闘中」に替わります。押すと戦闘の画面へ移ります。",
          "上に敵、真ん中に戦闘ログ、下に味方が並びます。",
        ],
      },
      {
        head: "行動の選び方",
        lines: [
          ["行動", "攻撃とスキルを選びます。相手を選ぶ行動は、続けて対象を選びます"],
          ["防御", "身を守ります"],
          ["道具", "持っている道具を使います"],
          ["逃亡", "戦闘から逃げます。押せないときは逃げられません"],
          "味方の全員の行動を選ぶと、決定 が出ます。押すとそのターンが進みます。戻る で選び直せます。",
          ["待ち・あと", "「待ち 2」は、使った後に 2 ターン使えないスキルです。「あと 1 ターン」は、使えるようになるまでの残りです"],
        ],
      },
      {
        head: "画面のボタン",
        lines: [
          ["敵と味方の枠", "押すと、スキル・装備・状態・耐性などの詳細が開きます"],
          ["×1", "戦闘ログの流れる速さを替えます"],
          ["早送り", "流れているログを最後まで飛ばします"],
          ["ログ", "ここまでの戦闘ログの全てを読みます"],
          ["≡", "詳細の一覧と、物語の画面へ戻る が出ます。途中で戻っても戦闘は残り、続きから再開できます"],
        ],
      },
      {
        head: "決着の後",
        lines: [
          "勝利・敗北・逃亡 のどれかが出ます。物語へ戻る を押します。",
          "結末は次の進行で物語になります。バーが「戦闘の結末を物語にします」の間は、そのまま待ちます。",
          "ほかのプレイヤーと一緒の戦闘では、「〜の行動を待っています」と出ることがあります。相手が選ぶと進みます。",
        ],
      },
    ],
  },
  {
    title: "作製",
    blocks: [
      {
        lines: [
          "物語の中で物を作り始めると、下のバーが「作製中」に替わります。押すと作製の画面へ移ります。",
          "上に 作る物・素材・道具 が出ます。問いが 1 つずつ出るので、選択肢から答えを選びます。",
          "答えるたびに手応えが出ます。全ての問いに答えると、出来が決まります。",
          "物語へ戻る を押します。結末は次の進行で物語になります。",
        ],
      },
    ],
  },
  {
    title: "その他の画面",
    blocks: [
      {
        head: "ログ",
        lines: [
          ["場面", "これまでの物語"],
          ["時間", "1 時間ごとの行動の記録"],
          ["取引", "物とお金の出入り"],
          ["戦闘・作製", "これまでの戦闘と作製の中身"],
          ["成長", "ステータスの伸びと、振り分けの記録"],
          ["入力", "確定した行動の文"],
          "ログの時刻は、世界共通の時間です。メインの日時は今いる場所の時刻なので、ずれることがあります。",
        ],
      },
      {
        head: "脚本設定",
        lines: [
          ["脚本設定", "物語の味付けです。9 つの項目を 5 段階で選び、テーマを書いて、保存する を押します"],
          ["執筆規定", "物語の文章の決まりです。このキャラの追加の規定 に、自分の決まりを足せます"],
          ["禁句設定", "物語に出したくない言葉を足せます"],
          "どれも次の進行から効きます。",
        ],
      },
      {
        head: "設定",
        lines: ["物語の文字サイズと表示を選びます。選んだ内容は、このキャラとこのブラウザに覚えます。"],
      },
      {
        head: "お知らせ",
        lines: ["進行が止まったときと、運営からの連絡があるときに届きます。画面の上の鈴のボタンから開きます。未読があると鈴に赤い印が付きます。"],
      },
      {
        head: "開発へメッセージ",
        lines: [
          ["FB 報告", "画面の不具合や、遊んでいて気づいたこと"],
          ["修正依頼", "物語やキャラの中身で直してほしい所"],
          ["手直し依頼", "キャラの設定で直したい所。結果はお知らせに届きます"],
          "送った後は取り消せません。",
        ],
      },
    ],
  },
  {
    title: "困ったとき",
    blocks: [
      {
        lines: [
          ["バーが開かない", "進行中・手直し中・停止中・戦闘の後 のどれかです。バーの文を見て、終わるのを待ちます"],
          ["確定したのに物語が届かない", "進行が始まるのを待っています。進行が止まったときは、お知らせに届きます"],
          ["書いたとおりにならない", "書いたことが必ず起こるとはかぎりません。失敗することも、できないこともあります"],
          ["装備を替えたのに変わらない", "付け替えは次の進行で物語に入ります"],
          ["スキルに「使えない」と出る", "そのスキルに要る装備を着けます"],
          ["物語やキャラの中身がおかしい", "その他 の 開発へメッセージ から、修正依頼 を送ります"],
          ["画面がおかしい", "その他 の 開発へメッセージ から、FB 報告 を送ります"],
        ],
      },
    ],
  },
];

function Lines({ lines, example }: { lines: Line[]; example?: boolean }) {
  return (
    <ul className="pl-prof-items">
      {lines.map((line, index) =>
        typeof line === "string" ? (
          <li key={index}>{line}</li>
        ) : example ? (
          <li key={index}>
            {line[0]}
            <small>{line[1]}</small>
          </li>
        ) : (
          <li key={index}>
            <b>{line[0]}</b>
            <span>{line[1]}</span>
          </li>
        )
      )}
    </ul>
  );
}

function Fold({ chapter }: { chapter: Chapter }) {
  return (
    <details className="pl-prof-sec pl-fold">
      <summary>{chapter.title}</summary>
      <div className="pl-fold-body">
        {chapter.blocks.map((block, index) => (
          <div className="pl-guide-block" key={index}>
            {block.head ? <p className="pl-guide-head">{block.head}</p> : null}
            <Lines lines={block.lines} example={block.example} />
          </div>
        ))}
      </div>
    </details>
  );
}

/**
 * 遊び方ガイドの画面。章を畳みで並べ、押した章だけを開く。
 * 行動の書き方の章は、入力シートのヘルプと同じ文面を出す。ほかの章の文面はこのファイルが持つ
 */
export default function PlayGuide({ help }: { help: Help }) {
  const writing: Chapter = {
    title: "行動の書き方",
    blocks: [
      { head: "書き方", example: true, lines: help.how.map(([line, example]): Line => (example ? [line, example] : line)) },
      { head: "注意点", lines: help.notes },
      { head: "おまかせオプション", lines: help.options },
      {
        head: "入力の欄",
        lines: [
          "書きかけは、バーを閉じたときに保存されます。画面を移っても、端末を替えても残ります。",
          "確定を取り消しても、書いた文は残ります。",
          "入力の欄の右上の ? を押すと、書き方をその場で読めます。",
        ],
      },
    ],
  };
  return (
    <div className="pl-guide">
      {BEFORE.map((chapter) => (
        <Fold chapter={chapter} key={chapter.title} />
      ))}
      <Fold chapter={writing} />
      {AFTER.map((chapter) => (
        <Fold chapter={chapter} key={chapter.title} />
      ))}
    </div>
  );
}
